import { useEffect, useMemo, useRef, useState } from 'react';

import { copy } from '../../core/copy';
import {
  currentIsland,
  earnedSouvenirs,
  islandBlocker,
  islandProgress,
  isIslandUnlocked,
  isNodeUnlocked,
  nextStep,
  shownNodes,
  nodeStatusOf,
  unlockedFeatures,
  visibleIslands,
} from '../../core/rules';
import { MAX_PEERS_PER_MARKER } from '../../core/posada';
import { decodeLook } from '../../core/look';
import { facingOfIndex, indexOfFacing } from '../../core/mesto';
import { GESTOVI, fromOffset, toOffset } from '../../core/soba';
import { Engine, type Direction, type IslandInfo, type MarkerInfo } from '../game/engine';
import { Uzivo, type SocketLike } from '../game/live';
import { WATER, buildWorld, islandAt, placePeers, tileAt } from '../game/world';
import { sfx } from '../audio';
import {
  contentOf,
  flushMesto,
  getMesto,
  loadPosadaView,
  mapView,
  openIsland,
  openNode,
  playerOf,
  refreshStateNow,
  setLiveIsland,
  setMesto,
  toast,
  useStore,
} from '../store';
import { PeerCard } from './Posada';

/**
 * The archipelago.
 *
 * Deliberately the second screen and never the first. It exists so the curriculum has a shape a
 * person can hold in their head — but a map of thirty-nine courses is exactly the overwhelm this
 * product is trying to cure, so only the islands already reached plus one are ever drawn. Past
 * that it is open water.
 */
export function Mapa() {
  const server = useStore((s) => s.server);
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const budget = useStore((s) => s.budget);
  const offerSkip = useStore((s) => s.offerSkip);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Engine | null>(null);
  // Seeded from module scope rather than from `false`: both of these used to be forgotten every time
  // somebody opened a task card, and the touch one took the d-pad with it. See `mapView`.
  const [touch, setTouch] = useState(mapView.touch);
  const [wide, setWide] = useState(mapView.wide);
  /** The four phrases, on a phone. Open only while A is held down, and never otherwise on screen. */
  const [gestures, setGestures] = useState(false);
  const holdTimer = useRef(0);
  const posadaView = useStore((s) => s.posadaView);
  const [peer, setPeer] = useState<string | null>(null);
  const hiddenList = useStore((s) => s.hidden);
  const liveRef = useRef<Uzivo | null>(null);
  // People appear on the map at the same gate as the crew screen (GDD §3): the second cleared
  // island, when somebody has been here long enough for other people to be interesting.
  const crewOn = unlockedFeatures(content, player).includes('posada');

  const model = useMemo(() => {
    const visible = visibleIslands(content, player);
    const step = nextStep(content, player, budget, offerSkip);
    // The gold arrow stands over whatever the card names — including the exercise offered at a
    // lock, which is a real next step even though the card's kind says "blocked".
    const nextNodeId =
      step.kind === 'rad' || step.kind === 'popravka'
        ? step.view.node.id
        : (step.kind === 'brava' || step.kind === 'kraj') && step.offer
          ? step.offer.node.id
          : null;

    const markers = new Map<string, MarkerInfo>();
    const islands = new Map<string, IslandInfo>();

    const entries = visible.map((island) => {
      const unlocked = isIslandUnlocked(content, player, island);
      const progress = islandProgress(island, player);
      const blocker = islandBlocker(content, player, island);
      islands.set(island.id, {
        name: island.name,
        biome: island.biome,
        locked: !unlocked,
        blocker: blocker ? blocker.text : null,
        done: progress.done,
        total: progress.total,
        tagline: island.free ? copy.island.free : null,
      });

      /**
       * On a free island this is the few on offer plus what has already been touched — never the
       * whole catalogue. The island's ground is unaffected: `buildWorld` lays it out from
       * `layoutIds`, which is every node on the island across every path, so the coastline, the
       * trail and every marker tile stay exactly where they are for everybody. Only which tiles get
       * a signpost changes — the same mechanism that lets an ADOBE-only task leave a bare trodden
       * spot that a DaVinci member walks over.
       */
      const nodes = unlocked ? shownNodes(content, player, island) : [];
      for (const node of nodes) {
        markers.set(node.id, {
          status: nodeStatusOf(node, player.progress[node.id]),
          title: node.title,
          locked: !isNodeUnlocked(content, player, island, node),
          next: node.id === nextNodeId,
        });
      }
      return { island, unlocked, visibleNodes: nodes };
    });

    const world = buildWorld(entries);
    /**
     * Where each island's centre sits in THIS world, which is the only thing that makes a position
     * mean the same on two screens.
     *
     * `world.origin` moves as somebody unlocks islands, so raw map coordinates are personal. The
     * island's authored centre is not, so everybody exchanges offsets from it and resolves them
     * against their own origin. This map is where that resolution happens, and it is rebuilt with
     * the world so it can never be one publish out of date.
     */
    const anchors = new Map<string, { x: number; y: number }>(
      visible.map((island) => [
        island.id,
        { x: island.map.x - world.origin.x, y: island.map.y - world.origin.y },
      ]),
    );
    return {
      world,
      markers,
      islands,
      anchors,
      nextNodeId,
      souvenirs: earnedSouvenirs(content, player),
    };
  }, [content, player, budget, offerSkip]);

  /**
   * Rebuilding the engine means regenerating the whole ground layer — tens of thousands of
   * fillRects and a multi-megabyte canvas. The tilemap only depends on which islands are visible
   * and how many nodes each shows, so key the engine on that signature rather than on the player
   * object, which is replaced on every single progress write.
   */
  const worldSignature = model.world.signature;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !server) return;

    /**
     * Where the character comes back.
     *
     * Three answers, in order, and the order is the whole fix.
     *
     * 1. **Where they were standing**, if it still resolves. `mesto` is an offset from an island's
     *    authored centre, so it is turned back into a tile against *this* world's origin — which is
     *    why it survives a new island appearing and shifting every coordinate on the map.
     * 2. The dock of the first island still open to them.
     * 3. The centre of the world, for an account with nothing unlocked at all.
     *
     * What is deliberately no longer first is „beside your next step". That was the old default and
     * it is what produced the reported bug: `nextStep` answers „where is your next task", which for
     * most of this community is an *earlier* island than the one they were exploring — and when there
     * is no next step at all it fell through to the lowest-order unlocked island, which is island
     * zero. Somebody who walked out to KRATKA FORMA and opened a card was returned to PLIĆAK.
     *
     * The next step has not lost its way of being found: the gold arrow still stands over it, and
     * SADA is still the screen whose entire job is to name it.
     */
    const remembered = getMesto();
    const anchor = remembered ? model.anchors.get(remembered.ostrvo) : undefined;
    const open = remembered ? model.islands.get(remembered.ostrvo) : undefined;
    const island = model.world.islands.find((i) => !i.locked);
    const start =
      anchor && open && !open.locked
        ? {
            ...fromOffset(anchor, { x: remembered!.dx, y: remembered!.dy }),
            facing: facingOfIndex(remembered!.f),
          }
        : (island?.dock ?? {
            x: Math.floor(model.world.width / 2),
            y: Math.floor(model.world.height / 2),
          });

    const engine = new Engine(
      canvas,
      model.world,
      model.markers,
      model.islands,
      decodeLook(server.member.look),
      model.souvenirs,
      start,
      {
        onInteract: (nodeId) => {
          const info = model.markers.get(nodeId);
          if (info?.locked) {
            sfx.bump();
            engine.say(copy.node.lockedHint);
            return;
          }
          openNode(nodeId);
        },
        onIslandTouch: (islandId) => {
          const info = model.islands.get(islandId);
          if (info?.locked) {
            sfx.bump();
            return;
          }
          openIsland(islandId);
        },
        onMove: () => {
          /* Position is remembered from `onLive` below, which also carries the facing. */
        },
        onPeer: (memberId) => setPeer(memberId),
        onLive: (tile, facing, moving) => {
          const live = liveRef.current;
          /**
           * Which island you are on, and therefore which room you are in.
           *
           * `islandAt` answers by bounding box, and a drawn island's box is its whole canvas —
           * several tiles of open sea included — so the box alone would put somebody in a room
           * while they were visibly at sea. The land test is what makes it mean anything: you are
           * on an island when its box contains you AND the tile under you is not water.
           *
           * The case left over is two islands whose boxes overlap while their coastlines do not.
           * That is legal content — the publish gate compares land, not canvases, deliberately —
           * and on such a tile `islandAt` answers by array order, which two viewers with different
           * horizons could resolve differently. Nobody is harmed by it (they simply would not see
           * each other), but if it ever has to be exact, the fix is to have `buildWorld` record
           * which island stamped each tile, not to test rectangles harder here.
           *
           * At sea you keep the room you left, which is both the friendlier answer (people rowing
           * away from an island can still see each other) and the one with no churn: a crossing
           * never reconnects.
           */
          const under = islandAt(model.world, tile.x, tile.y);
          const open =
            under && !model.islands.get(under.islandId)?.locked ? under.islandId : null;
          const onLand = open !== null && !WATER.has(tileAt(model.world, tile.x, tile.y));

          /**
           * Remember it — and note where this sits relative to the `live` guard below.
           *
           * This runs for everybody, including the majority who have not cleared two islands and
           * therefore have no room and no socket at all. Putting the save after `if (!live) return`
           * would have quietly fixed the teleport only for members who already have the crew, which
           * is the smaller half of the community and not the half that gets lost.
           *
           * Recorded only while standing on an island whose centre this world knows. At sea there is
           * nothing to measure from, so the last landfall is what gets kept — which is also the
           * friendlier restore: nobody wants to come back bobbing in open water.
           */
          const here = open ? model.anchors.get(open) : undefined;
          if (open && here) {
            const offset = toOffset(here, tile);
            setMesto({ ostrvo: open, dx: offset.x, dy: offset.y, f: indexOfFacing(facing) });
          }

          if (!live) return;

          /**
           * Landfall switches rooms — and the very first report joins one whatever it lands on.
           *
           * Without that second clause, somebody who opens the map and does not move is never in
           * any room at all: the map spawns them on the dock, whose outer tile is water and, on a
           * generated island, one row outside the island's own bounds. They would be invisible to
           * everybody until they happened to take a step, which is exactly the member most worth
           * showing — the one who just arrived.
           */
          const target = onLand
            ? open
            : live.room()
              ? null
              : (open ?? currentIsland(content, player).id);
          const anchor = target ? model.anchors.get(target) : undefined;
          if (target && anchor) {
            live.enter(target, anchor, content.version);
            // So a wardrobe save can tell the room this member is actually standing in, rather
            // than the one their next step happens to be on.
            setLiveIsland(target);
          }
          live.report(tile, facing, moving);
        },
      },
    );

    engineRef.current = engine;
    // A fresh Engine always starts close-up, so a rebuild while the map is open used to leave React
    // holding `wide === true` over an engine that had zoomed itself back in — the button then read
    // „PRIBLIŽI" and did the opposite. Restate the zoom on every construct instead.
    if (wide) engine.setView('pregled');
    engine.start();

    const keyMap: Record<string, Direction> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
      w: 'up',
      s: 'down',
      a: 'left',
      d: 'right',
      W: 'up',
      S: 'down',
      A: 'left',
      D: 'right',
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      const direction = keyMap[event.key];
      if (direction) {
        event.preventDefault();
        engine.press(direction);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        engine.interact();
        return;
      }
      // One of the four, on the number keys. No button appears anywhere: a map that already
      // carries a d-pad, an A button and two controls on a 375px phone has no room to spend on a
      // feature nobody has to use, and a member who never presses these never learns they exist —
      // which is the correct amount of attention for the least important thing on the screen.
      // `repeat` matters: a held number key fires thirty times a second, and without this the room
      // would answer a leaned-on keyboard by closing the socket for talking too fast — dropping
      // everybody off the map of the person who did it.
      if (event.repeat) return;
      const gesture = Number(event.key) - 1;
      if (Number.isInteger(gesture) && gesture >= 0 && gesture < GESTOVI.length) {
        event.preventDefault();
        liveRef.current?.gesture(gesture);
      }
      // A conversation is the only thing on this screen Escape can close, and leaving it with no
      // way out but reading to the end is the wrong bargain for somebody who bumped into it.
      if (event.key === 'Escape' && engine.inConversation()) {
        event.preventDefault();
        engine.closeDialogue();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      const direction = keyMap[event.key];
      if (direction) engine.release(direction);
    };
    const onBlur = () => engine.releaseAll();
    const onResize = () => engine.resize();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', onResize);
    const observer = new ResizeObserver(() => engine.resize());
    observer.observe(canvas);

    const onFirstTouch = () => {
      mapView.touch = true;
      setTouch(true);
    };
    canvas.addEventListener('touchstart', onFirstTouch, { once: true, passive: true });

    return () => {
      engine.stop();
      engineRef.current = null;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', onResize);
      observer.disconnect();
      canvas.removeEventListener('touchstart', onFirstTouch);
      // Leaving the map is the moment worth writing down: the record is complete, and the alternative
      // is losing up to ten seconds of walking to the flush interval.
      flushMesto();
    };
    // `server.member.look` is deliberately NOT a dependency: an appearance change goes in through
    // `setLook` below. It used to be listed here, which meant a haircut regenerated the whole ground
    // layer and re-spawned the character while the member watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldSignature, server?.member.id]);

  // A new face, into the running engine. See `Engine.setLook`.
  useEffect(() => {
    if (server) engineRef.current?.setLook(decodeLook(server.member.look));
  }, [server?.member.look, worldSignature]);

  /**
   * The other way a tab ends.
   *
   * `pagehide` is the only event a phone reliably fires when the browser is backgrounded and then
   * killed, and it is the difference between "continue where you left off on your laptop" working and
   * working only when somebody remembers to press a nav button first.
   */
  useEffect(() => {
    const onHide = () => flushMesto();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Node states change on every write; the tilemap does not. Push the new state into the running
  // engine instead of letting the rebuild effect fire. Souvenirs ride along: clear an island with
  // the map open and the character is wearing the souvenir on the very next frame.
  useEffect(() => {
    engineRef.current?.refresh(model.markers, model.islands, model.souvenirs);
  }, [model]);

  /**
   * The crew, on a clock of its own.
   *
   * Fetched after the map is already drawn and pushed in when it lands, so the archipelago never
   * waits on other people to appear — and if the request fails, the map is exactly the map it was
   * before this feature existed.
   */
  useEffect(() => {
    if (!crewOn) return;
    void loadPosadaView();
    const timer = window.setInterval(() => void loadPosadaView(), 45_000);
    return () => window.clearInterval(timer);
  }, [crewOn]);

  useEffect(() => {
    if (!crewOn || !posadaView) {
      engineRef.current?.setPeers([], new Map());
      return;
    }
    const { placed, crowds } = placePeers(model.world, posadaView.peers, MAX_PEERS_PER_MARKER);
    engineRef.current?.setPeers(placed, crowds);
  }, [crewOn, posadaView, model.world]);

  /**
   * The live room.
   *
   * Its lifetime is the screen's, not the tilemap's — the engine is thrown away and rebuilt every
   * time an island comes into view, and tearing down a working socket to redraw a coastline would
   * drop everybody off the island for a second every time somebody finished a task. So it is
   * created once, handed to whichever engine is current, and closed when the map is left.
   *
   * Same gate as the crew (GDD §3): before the second cleared island no socket is opened at all.
   */
  useEffect(() => {
    if (!crewOn) return;
    const live = new Uzivo({
      open: (url) => new WebSocket(url) as unknown as SocketLike,
      /*
       * A challenge, landing while the two of them are standing next to each other.
       *
       * It refreshes state rather than opening anything: the arena is one tap away on the card that
       * appears, and a screen that takes itself over because somebody else pressed a button is the
       * one thing GDD §2① rules out. The sound is the whole notification.
       */
      onDuel: () => {
        sfx.izazov();
        void refreshStateNow();
      },
    });
    liveRef.current = live;
    engineRef.current?.setLive(live);
    return () => {
      live.leave();
      liveRef.current = null;
      setLiveIsland(null);
      engineRef.current?.setLive(null);
    };
  }, [crewOn]);

  // The engine is the one that gets rebuilt, so it has to be re-handed the room it did not own.
  useEffect(() => {
    engineRef.current?.setLive(liveRef.current);
    engineRef.current?.setHidden(new Set(hiddenList));
  }, [worldSignature, hiddenList]);

  /**
   * Islands do not move, but the coordinates they are drawn at do — the world's origin shifts as
   * the horizon opens. Re-anchoring on every rebuild is what keeps everybody standing where their
   * owner is standing across the moment a new island appears.
   */
  useEffect(() => {
    const live = liveRef.current;
    const engine = engineRef.current;
    if (!live || !engine) return;
    const here = islandAt(model.world, engine.position().x, engine.position().y);
    const anchor = here ? model.anchors.get(here.islandId) : undefined;
    if (anchor) live.reanchor(anchor);
  }, [model]);

  if (!server) return null;

  /**
   * Who the card is about.
   *
   * The crew view is preferred — it knows which island they are on in curriculum terms — but it is
   * fetched on a slow clock and filtered by shared islands and a thirty-day window, so somebody you
   * have just walked up to can legitimately be missing from it. Falling back to what the room
   * itself vouched for means a meeting never opens an empty card, which would be the one moment
   * this whole feature exists for, failing silently.
   */
  const peerCard = (() => {
    if (!peer) return null;
    const known = posadaView?.peers.find((p) => p.id === peer);
    const body = liveRef.current?.find(peer);
    if (!body) return known ?? null;
    /**
     * Somebody standing in front of you is on THIS island, whatever the crew view says.
     *
     * The crew view puts everybody at their next unfinished task, which is the right answer for a
     * person you have not met — it is where they are in the curriculum. It is the wrong answer for
     * a person you are looking at: somebody who has finished this island and come back to help, or
     * who is simply walking about, would have their card announce an island they are not on. Live
     * truth wins for where they are; the crew view still answers what they are working on.
     */
    return {
      id: body.id,
      name: body.name,
      look: body.look,
      souvenirs: [...body.souvenirs],
      nodeId: body.nodeId ?? known?.nodeId ?? null,
      islandId: liveRef.current?.room() ?? known?.islandId ?? null,
    };
  })();

  const hold = (direction: Direction) => ({
    onPointerDown: (event: React.PointerEvent) => {
      event.preventDefault();
      engineRef.current?.press(direction);
    },
    onPointerUp: () => engineRef.current?.release(direction),
    onPointerLeave: () => engineRef.current?.release(direction),
    onPointerCancel: () => engineRef.current?.release(direction),
  });

  return (
    <div className="map-wrap">
      <canvas
        ref={canvasRef}
        className="map-canvas"
        role="img"
        aria-label={copy.a11y.map}
        tabIndex={0}
        onClick={(event) => engineRef.current?.pointAt(event.clientX, event.clientY)}
      />

      <div className="map-legend">
        {touch ? copy.map.hintTouch : copy.map.hint}
        {/* Mentioned only once the crew exists, and only in the line that was already there. */}
        {crewOn ? ` ${touch ? copy.uzivo.hintTouch : copy.uzivo.hint}` : ''}
      </div>

      <div className="map-controls">
        <button
          className="btn ghost small"
          aria-pressed={wide}
          onClick={() => {
            const next = wide ? 'hod' : 'pregled';
            engineRef.current?.setView(next);
            mapView.wide = !wide;
            setWide(!wide);
          }}
        >
          {wide ? copy.map.zoomIn : copy.map.zoomOut}
        </button>
        <button
          className="btn ghost small"
          onClick={() => {
            // The island the student is standing on, not the first one they ever unlocked.
            const here = currentIsland(content, player);
            if (here) openIsland(here.id);
            else toast(copy.map.horizonHint);
          }}
        >
          {copy.a11y.listView}
        </button>
      </div>

      {touch ? (
        <>
          <div className="dpad" aria-hidden="true">
            <span className="blank" />
            <button {...hold('up')}>↑</button>
            <span className="blank" />
            <button {...hold('left')}>←</button>
            <span className="blank" />
            <button {...hold('right')}>→</button>
            <span className="blank" />
            <button {...hold('down')}>↓</button>
            <span className="blank" />
          </div>
          {/* Hold A for the four phrases. Nothing new appears until it is held, so the phone keeps
              the controls it had — the overwhelm this whole product exists to cure is measured in
              things on screen, and a fifth button would be one. */}
          <button
            className="abutton"
            onClick={() => {
              if (gestures) setGestures(false);
              else engineRef.current?.interact();
            }}
            onPointerDown={() => {
              holdTimer.current = window.setTimeout(() => setGestures(true), 420);
            }}
            onPointerUp={() => window.clearTimeout(holdTimer.current)}
            onPointerLeave={() => window.clearTimeout(holdTimer.current)}
            onPointerCancel={() => window.clearTimeout(holdTimer.current)}
            aria-hidden="true"
          >
            A
          </button>
          {gestures ? (
            <div className="gestovi" aria-hidden="true">
              {GESTOVI.map((gest, index) => (
                <button
                  key={gest}
                  onClick={() => {
                    liveRef.current?.gesture(index);
                    setGestures(false);
                  }}
                >
                  {copy.uzivo.gestovi[gest]}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {/* Walked into somebody. The same card the crew screen opens, so there is one idea of what
          a person is and one place their posts are answered. */}
      {peerCard ? (
        <PeerCard
          peer={peerCard}
          works={posadaView?.works.filter((work) => work.memberId === peerCard.id) ?? []}
          onClose={() => setPeer(null)}
        />
      ) : null}
    </div>
  );
}
